'use client'

import React from 'react'
import { Drawer } from '@mantine/core'
import { RiCloseLine } from '@remixicon/react'
import { usePathname } from 'next/navigation'
import { hamburgerMenuStore } from '../../../shared/Header/Store/hamburgerMenuStore'
import { NavItem } from './NavItem'
import { DashboardNavGroup, isNavItemActive } from './navigation'
import drawerClasses from './DashboardNavDrawer.module.css'

export interface DashboardNavDrawerProps {
  navGroups: DashboardNavGroup[]
  /** Support card, logout, schedule-a-call — whatever belongs at the bottom. */
  footer?: React.ReactNode
  closeLabel?: string
}

/**
 * The full-screen mobile navigation.
 *
 * Distinct from @repo/ui's shared HamburgerMenu, which is the cross-app global
 * menu five other apps render. This one is the dashboard's own nav, driven by
 * the same groups as the sidebar, so a phone reaches every destination the
 * desktop does — including the ones the bottom bar has no room for.
 *
 * Shares hamburgerMenuStore with the top header and the tab bar's "More", so
 * all three drive one surface.
 */
export const DashboardNavDrawer: React.FC<DashboardNavDrawerProps> = ({
  navGroups,
  footer,
  closeLabel = 'Close menu',
}) => {
  const { opened, close } = hamburgerMenuStore()
  const pathname = usePathname()

  return (
    <Drawer
      offset={0}
      opened={opened}
      size="100%"
      onClose={close}
      position="right"
      classNames={drawerClasses}
      withCloseButton={false}
      radius="lg"
    >
      <div className="flex flex-col justify-between h-full p-3">
        <div className="w-full">
          <div className="flex items-center justify-end w-full mt-4">
            <button
              type="button"
              onClick={close}
              aria-label={closeLabel}
              className="w-11 h-11 flex items-center justify-center bg-white rounded-2xl text-black"
            >
              <RiCloseLine size={20} />
            </button>
          </div>

          <nav className="flex flex-col w-full gap-4 mt-4">
            {navGroups.map((group) => (
              <div key={group.id} className="flex flex-col gap-[2px]">
                {group.label && (
                  <div className="px-3 pb-1 text-[11px] font-semibold uppercase tracking-[0.04em] text-dashboard-axis">
                    {group.label}
                  </div>
                )}
                {group.items.map((item) => (
                  <NavItem
                    key={item.id}
                    variant="drawer"
                    icon={item.icon}
                    label={item.label}
                    href={item.href}
                    badge={item.badge}
                    external={item.external}
                    onClick={close}
                    active={isNavItemActive(pathname, item)}
                  />
                ))}
              </div>
            ))}
          </nav>
        </div>

        {footer && <div className="pt-4">{footer}</div>}
      </div>
    </Drawer>
  )
}
